import { useContext, useEffect, useState } from "react";

import Button from "~/components/Button/Button";
import Dialog from "~/components/Dialog/Dialog";

import styles from "./MyAccountModal.module.css";
import { useFetcher } from "@remix-run/react";
import { KEYS } from "~/modules/checkout/constants";
import Checkbox from "~/components/Checkbox/Checkbox";
import AuthContext from "~/contexts/AuthContext";
import Tabs from "~/components/Tabs/Tabs";
import PersonalDetailsForm from "~/modules/checkout/components/PersonalDetailsForm/PersonalDetailsForm";
import compact from "lodash/compact";
import CircleCloseButton from "~/icons/CircleCloseIcon";
import EmptyState from "~/components/EmptyState/EmptyState";
import CardIcon from "~/icons/CardIcon";
import { brandConfig } from "~/configs/brandConfigs";
import { Brand } from "~/entities/brandEntities";

interface MyAccountModalProps {
  visible: boolean;
  onClose: () => void;
}

const MyAccountModal: React.FC<MyAccountModalProps> = ({
  visible,
  onClose,
}) => {
  const { authenticatedUser, useComoAuth } = useContext(AuthContext);

  const fetcher = useFetcher({ key: KEYS.MY_ACCOUNT_FETCHER });

  const [loyaltyCheckbox, setLoyaltyCheckbox] = useState<boolean>(
    authenticatedUser?.loyaltyCheckbox ?? false,
  );
  const [allowEmail, setAllowEmail] = useState<boolean>(
    authenticatedUser?.allowEmail ?? false,
  );

  useEffect(() => {
    if (authenticatedUser) {
      setLoyaltyCheckbox(authenticatedUser.loyaltyCheckbox ?? false);
      setAllowEmail(authenticatedUser.allowEmail ?? false);
    }
  }, [authenticatedUser]);

  return (
    <Dialog visible={visible} onClose={onClose}>
      <div className={styles.container}>
        <div className={styles.header}>
          <span className={styles.title}>My Account</span>
          <CircleCloseButton color="#828282" onClick={onClose} />
        </div>

        <div className={styles.content}>
          <Tabs
            tabs={compact([
              {
                label: "My Details",
                content: (
                  <div className={styles.tabContent}>
                    <PersonalDetailsForm
                      onSubmit={(values) => {
                        const formData = new FormData();
                        formData.append("firstName", values.firstName);
                        formData.append("lastName", values.lastName);
                        formData.append("email", values.email);

                        fetcher.submit(formData, {
                          method: "POST",
                          action: "/auth/myAccount",
                        });
                      }}
                      isEditingAccount={true}
                      classNameFooter={styles.personalDetailsFooter}
                      loading={fetcher.state !== "idle"}
                    />
                  </div>
                ),
              },
              {
                label: "Notifications",
                hide: !useComoAuth,
                content: (
                  <div className={styles.tabContent}>
                    <div
                      className={styles.checkboxItem}
                      onClick={() => setLoyaltyCheckbox(!loyaltyCheckbox)}
                    >
                      <Checkbox checked={loyaltyCheckbox} required />
                      <div className={styles.checkboxText}>
                        <b>Opt in to our loyalty programme</b>
                        <span>
                          {brandConfig.Brand === Brand.BURGER_FUEL
                            ? "Get access to exclusive VIB Club offers, promos, prizes & more"
                            : "Get access to sweet perks and exclusive deals, promos, prizes and more"}
                        </span>
                      </div>
                    </div>
                    <div
                      className={styles.checkboxItem}
                      onClick={() => setAllowEmail(!allowEmail)}
                    >
                      <Checkbox checked={allowEmail} required />
                      <div className={styles.checkboxText}>
                        <b>Receive email notifications</b>
                        <span>
                          {brandConfig.Brand === Brand.BURGER_FUEL
                            ? "Get notified of new offers in your account & get the latest BurgerFuel news"
                            : "Get notified on the latest Shake Out news, perks offers, promotions and more"}
                        </span>
                      </div>
                    </div>
                    <div className={styles.buttons}>
                      <Button
                        onClick={() => {
                          const formData = new FormData();
                          formData.append(
                            "loyaltyCheckbox",
                            loyaltyCheckbox.toString(),
                          );
                          formData.append("allowEmail", allowEmail.toString());

                          fetcher.submit(formData, {
                            method: "POST",
                            action: "/auth/myAccount",
                          });
                        }}
                        loading={fetcher.state !== "idle"}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                ),
              },
              {
                label: "My Cards",
                hide: !useComoAuth,
                content: (
                  <div className={styles.tabContent}>
                    <EmptyState
                      icon={CardIcon}
                      title="No saved cards"
                      description="You don't have any saved cards yet."
                    />
                  </div>
                ),
              },
            ])}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default MyAccountModal;
